import { ArrowRight, Building2, CheckCircle2, LockKeyhole, ShieldCheck } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { useApp } from "../context/AppContext";

type LoginMode = "checking" | "accounts" | "legacy" | "unavailable";

export function LoginPage() {
  const { login } = useApp();
  const [mode, setMode] = useState<LoginMode>("checking");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Accounts is the single login page: go there directly, once per minute to avoid loops.
  useEffect(() => {
    if (mode !== "accounts") return;
    let last = 0;
    let manual = false;
    try {
      last = Number(sessionStorage.getItem("sso-redirect-at") || 0);
      manual = sessionStorage.getItem("sso-manual") === "1";
    } catch { /* storage off */ }
    if (manual || Date.now() - last < 60_000) return;
    try { sessionStorage.setItem("sso-redirect-at", String(Date.now())); } catch { /* storage off */ }
    window.location.replace("/api/v1/auth/oidc/start/");
  }, [mode]);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/v1/auth/oidc/status/", {
      credentials: "same-origin",
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error("Service de connexion indisponible.");
        return response.json() as Promise<{ enabled: boolean; available: boolean }>;
      })
      .then((result) => {
        if (!controller.signal.aborted) {
          setMode(result.enabled ? (result.available ? "accounts" : "unavailable") : "legacy");
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setMode("unavailable");
      });
    return () => controller.abort();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mode !== "legacy") return;
    setBusy(true);
    setError("");
    try {
      await login(username, password);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Connexion impossible.");
    } finally {
      setBusy(false);
    }
  }

  if (mode === "checking" || mode === "accounts") {
    return (
      <main className="grid min-h-screen place-items-center bg-[#F6F8FB] px-4 text-sm text-[#52697E]">
        {mode === "accounts"
          ? <a href="/api/v1/auth/oidc/start/" onClick={() => { try { sessionStorage.removeItem("sso-manual"); } catch { /* storage off */ } }} className="font-bold text-[#004A87]">Se connecter</a>
          : <span role="status">Connexion…</span>}
      </main>
    );
  }

  return (
    <main className="grid min-h-screen grid-cols-1 bg-[#F6F8FB] lg:grid-cols-[1.04fr_0.96fr]">
      <section className="relative isolate flex flex-col overflow-hidden bg-[#0A192F] px-6 pb-8 pt-8 text-white sm:px-12 lg:min-h-screen lg:px-16 lg:py-12">
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-12 -z-10 h-[450px] w-[450px] rounded-full bg-[#004A87]/30 blur-[110px]" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 -z-10 h-[320px] w-[320px] rounded-full bg-[#E67E22]/10 blur-[100px]" />
        <a className="inline-flex w-fit items-center gap-3 rounded-2xl bg-white px-4 py-2.5 text-[#0A192F] shadow-sm" href="https://saheltech.tech/" aria-label="SahelTech, site officiel">
          <img src="/saheltech-mark.svg" alt="" className="h-10 w-14 object-contain" />
          <span className="text-lg font-black tracking-tight">SahelTech</span>
        </a>
        <div className="my-auto max-w-xl pb-4 pt-12 lg:py-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FFAD60]/30 bg-[#E67E22]/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.17em] text-[#FFCE9B]">
            <span className="h-2 w-2 rounded-full bg-[#E67E22]" /> Votre espace professionnel
          </div>
          <h1 className="mt-6 text-4xl font-black leading-[1.13] tracking-[-0.045em] sm:text-5xl lg:text-[clamp(38px,4.15vw,65px)]">
            Votre entreprise.
            <span className="block text-[#FFAD60]">Toutes vos activités.</span>
          </h1>
          <p className="mt-6 max-w-lg text-base leading-8 text-slate-300">
            Un seul compte SahelTech pour piloter vos entreprises, vos équipes, vos projets et vos applications métier.
          </p>
          <div className="mt-9 hidden space-y-4 text-sm text-slate-200 sm:block">
            <div className="flex items-center gap-3"><Building2 className="h-5 w-5 text-[#FFAD60]" /> Plusieurs entreprises, chacune dans son propre espace</div>
            <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#FFAD60]" /> Droits et informations séparés par entreprise</div>
            <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#FFAD60]" /> Google ou email depuis votre compte SahelTech</div>
          </div>
        </div>
        <div className="hidden items-center justify-between border-t border-white/10 pt-5 text-xs text-slate-400 lg:flex">
          <span>SahelTech · Mali / UEMOA</span>
          <span>Votre activité, votre espace.</span>
        </div>
      </section>

      <section className="flex min-w-0 items-center justify-center px-5 py-10 sm:px-10 lg:py-16" aria-label="Connexion à SahelTech Platform">
        <div className="w-full max-w-[465px] rounded-[28px] border border-[#E1E8EF] bg-white p-7 shadow-[0_26px_80px_rgba(10,25,47,0.055)] sm:p-10">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full bg-[#EDF4FB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#004A87]">
            <LockKeyhole className="h-3.5 w-3.5" /> Accès sécurisé
          </div>
          <h2 className="text-3xl font-black tracking-tight text-[#0A192F] sm:text-[36px]">Connexion</h2>
          <p className="mt-3 text-sm leading-6 text-[#65768A]">Bienvenue sur votre espace SahelTech Platform.</p>

          {mode === "legacy" && (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="platform-username" className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#425972]">Nom d'utilisateur</label>
                <input id="platform-username" className="field" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
              </div>
              <div>
                <label htmlFor="platform-password" className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#425972]">Mot de passe</label>
                <input id="platform-password" className="field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
              </div>
              {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
              <button disabled={busy} type="submit" className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#004A87] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#073A68]">
                {busy ? "Connexion…" : "Se connecter"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-center text-xs leading-6 text-[#718494]">Mode de connexion historique. Le compte central SahelTech sera activé après validation.</p>
              <p className="border-t border-[#E4EBF2] pt-5 text-center text-sm text-[#64778A]">
                Nouvelle entreprise ? <a href="https://plateform.saheltech.tech/fr/commencer" className="font-bold text-[#004A87] hover:underline">Commencer avec SahelTech</a>
              </p>
            </form>
          )}

          {mode === "unavailable" && (
            <div role="alert" className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              La connexion est temporairement indisponible. Aucune autre méthode ne sera utilisée sans vérification.
            </div>
          )}

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-[#8191A0]">
            <ShieldCheck className="h-4 w-4 text-[#004A87]" /> SahelTech, votre identité au cœur de l'écosystème.
          </div>
        </div>
      </section>
    </main>
  );
}
